import React from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/apiClient";
import AdminShell from "../admin/AdminShell";

const LOCALES = ["en", "fr"];
const PAGE_TABS = [
  ["terms", "Terms & conditions", "/legal/terms"],
  ["privacy", "Privacy", "/legal/privacy"],
  ["about", "About", "/about"],
  ["pricing", "Pricing", "/pricing"],
  ["configurator", "Configurator", "/configurator"],
];

export default function AdminPages({ onSaved, embedded = false }) {
  const [page, setPage] = React.useState("terms");
  const [content, setContent] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    api
      .get("/content")
      .then((r) => setContent(r.data))
      .catch(() => toast.error("Could not load pages."));
  }, []);

  const editor = !content ? (
    <div className="rounded-2xl border border-dashed border-zinc-300 p-12 text-center text-zinc-500">Loading pages…</div>
  ) : (
    <AdminPagesEditor
      page={page}
      setPage={setPage}
      content={content}
      setContent={setContent}
      saving={saving}
      setSaving={setSaving}
      onSaved={onSaved}
    />
  );

  if (embedded) return editor;

  return (
    <AdminShell>
      <div className="mb-6">
        <h2 className="text-xl font-display font-bold text-oj-ink">Pages</h2>
        <p className="text-sm text-zinc-500 mt-0.5">
          Edit live copy for terms, privacy, about, pricing, and the configurator. This is the CMS
          (stored in <code className="text-oj-ink">content.json</code>). Mailco keys live under Settings,
          not here. The Why O&#39;javana story at <code>/why</code> is a separate marketing page.
        </p>
      </div>
      {editor}
    </AdminShell>
  );
}

function AdminPagesEditor({ page, setPage, content, setContent, saving, setSaving, onSaved }) {
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/content", content);
      setContent(data);
      toast.success("Pages saved. Live site will use the new copy.");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Not signed in" : formatApiError(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const legal = content.legal?.[page] || { en: { title: "", body: "" }, fr: { title: "", body: "" } };
  const setLegalLocale = (locale, field, value) => {
    setContent((c) => {
      const current = c.legal?.[page] || {};
      const loc = current[locale] || { title: "", body: "" };
      return {
        ...c,
        legal: {
          ...c.legal,
          [page]: {
            ...current,
            [locale]: { ...loc, [field]: value },
          },
        },
      };
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-pages">
      <div className="flex flex-wrap gap-2">
        {PAGE_TABS.map(([id, label]) => (
          <button
            key={id}
            type="button"
            data-testid={`admin-page-${id}`}
            onClick={() => setPage(id)}
            className={`text-sm font-semibold px-3 py-2 rounded-md border ${
              page === id ? "bg-oj-ink text-white border-oj-ink" : "border-zinc-200 hover:border-zinc-900"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <p className="text-xs text-zinc-500">
        Live URL:{" "}
        <a
          href={PAGE_TABS.find(([id]) => id === page)?.[2] || "#"}
          target="_blank"
          rel="noreferrer"
          className="font-semibold text-oj-ink underline underline-offset-2"
        >
          {PAGE_TABS.find(([id]) => id === page)?.[2]}
        </a>
      </p>

      {(page === "terms" || page === "privacy" || page === "about") && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {LOCALES.map((locale) => (
            <article key={locale} className="rounded-2xl border border-zinc-200 bg-white p-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{locale}</div>
              <input
                value={legal[locale]?.title || ""}
                onChange={(e) => setLegalLocale(locale, "title", e.target.value)}
                className="mt-3 w-full bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2 text-sm font-semibold"
                placeholder="Page title"
              />
              <textarea
                value={legal[locale]?.body || ""}
                onChange={(e) => setLegalLocale(locale, "body", e.target.value)}
                rows={16}
                className="mt-3 w-full bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2 text-sm leading-relaxed"
                placeholder="Page body"
              />
            </article>
          ))}
        </div>
      )}

      {page === "pricing" && (
        <div className="rounded-2xl border border-zinc-200 bg-white p-5 space-y-4">
          <p className="text-sm text-zinc-600">Monthly software prices in CHF per station (before terminal / yearly toggles).</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {(content.pricing?.plans || []).map((plan, idx) => (
              <label key={plan.id} className="block text-sm">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{plan.id}</span>
                <input
                  type="number"
                  value={plan.monthly}
                  onChange={(e) => {
                    const monthly = Number(e.target.value);
                    setContent((c) => {
                      const plans = [...c.pricing.plans];
                      plans[idx] = { ...plans[idx], monthly };
                      return { ...c, pricing: { ...c.pricing, plans } };
                    });
                  }}
                  className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2"
                />
              </label>
            ))}
          </div>
          <label className="block text-sm max-w-xs">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Kiosk add-on price</span>
            <input
              type="number"
              value={content.pricing?.kiosk_addon_price ?? 1980}
              onChange={(e) =>
                setContent((c) => ({
                  ...c,
                  pricing: { ...c.pricing, kiosk_addon_price: Number(e.target.value) },
                }))
              }
              className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2"
            />
          </label>
        </div>
      )}

      {page === "configurator" && (
        <div className="space-y-4">
          <label className="block text-sm max-w-xs">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Software CHF / station / month</span>
            <input
              type="number"
              value={content.configurator?.software_price_per_station ?? 69}
              onChange={(e) =>
                setContent((c) => ({
                  ...c,
                  configurator: { ...c.configurator, software_price_per_station: Number(e.target.value) },
                }))
              }
              className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2"
            />
          </label>
          <div className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                <tr>
                  <th className="px-3 py-2">SKU</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Price</th>
                  <th className="px-3 py-2">Default qty</th>
                </tr>
              </thead>
              <tbody>
                {(content.configurator?.hardware || []).map((row, idx) => (
                  <tr key={row.sku || idx} className="border-t border-zinc-100">
                    <td className="px-3 py-2 font-mono text-xs">{row.sku}</td>
                    <td className="px-3 py-2">
                      <input
                        value={row.name}
                        onChange={(e) => {
                          const name = e.target.value;
                          setContent((c) => {
                            const hardware = [...c.configurator.hardware];
                            hardware[idx] = { ...hardware[idx], name };
                            return { ...c, configurator: { ...c.configurator, hardware } };
                          });
                        }}
                        className="w-full bg-zinc-50 border border-zinc-200 rounded px-2 py-1"
                      />
                    </td>
                    <td className="px-3 py-2 w-28">
                      <input
                        type="number"
                        value={row.price}
                        onChange={(e) => {
                          const price = Number(e.target.value);
                          setContent((c) => {
                            const hardware = [...c.configurator.hardware];
                            hardware[idx] = { ...hardware[idx], price };
                            return { ...c, configurator: { ...c.configurator, hardware } };
                          });
                        }}
                        className="w-full bg-zinc-50 border border-zinc-200 rounded px-2 py-1"
                      />
                    </td>
                    <td className="px-3 py-2 w-24">
                      <input
                        type="number"
                        value={row.default_qty ?? 0}
                        onChange={(e) => {
                          const default_qty = Number(e.target.value);
                          setContent((c) => {
                            const hardware = [...c.configurator.hardware];
                            hardware[idx] = { ...hardware[idx], default_qty };
                            return { ...c, configurator: { ...c.configurator, hardware } };
                          });
                        }}
                        className="w-full bg-zinc-50 border border-zinc-200 rounded px-2 py-1"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <button
        type="button"
        data-testid="admin-pages-save"
        onClick={save}
        disabled={saving}
        className="inline-flex items-center bg-oj-ink text-white font-semibold px-5 py-3 rounded-md hover:bg-zinc-800 disabled:opacity-60"
      >
        {saving ? "Saving…" : "Save changes"}
      </button>
    </div>
  );
}
